import { useState, useCallback, useEffect, useRef } from 'react';
import type { GeneratedPlaylist, TrackResolutionResult, PlaybackState } from '../types';
import {
  DEV_MODE,
  initDevMode,
  mockMusicKitInstance,
  mockResolveIsrcs,
  mockCreatePlaylist,
} from '../lib/dev-stubs';

interface MusicKitState {
  isConfigured: boolean;
  isAuthorized: boolean;
  isLoading: boolean;
  error: string | null;
  storefrontId: string | null;
  // Playback state
  playbackState: PlaybackState;
  isPlaying: boolean;
  isPreviewMode: boolean;
  currentTrackIndex: number;
  currentArtworkUrl: string | null;
}

interface UseMusicKitReturn extends MusicKitState {
  configure: () => Promise<void>;
  authorize: () => Promise<boolean>;
  unauthorize: () => Promise<void>;
  createPlaylist: (playlist: GeneratedPlaylist) => Promise<string | null>;
  resolveIsrcs: (isrcs: string[]) => Promise<TrackResolutionResult>;
  // Playback methods
  setQueue: (catalogIds: string[]) => Promise<void>;
  play: () => Promise<void>;
  pause: () => void;
  skipToNext: () => Promise<void>;
  skipToPrevious: () => Promise<void>;
  skipToTrack: (index: number) => Promise<void>;
}

const MUSICKIT_SCRIPT_URL = 'https://js-cdn.music.apple.com/musickit/v3/musickit.js';

// MusicKit playback states (from MusicKit.PlaybackStates)
const PlaybackStates = {
  none: 0,
  loading: 1,
  playing: 2,
  paused: 3,
  stopped: 4,
  ended: 5,
  seeking: 6,
  waiting: 7,
  stalled: 8,
  completed: 9,
} as const;

function mapPlaybackState(mkState: number): PlaybackState {
  switch (mkState) {
    case PlaybackStates.playing:
      return 'playing';
    case PlaybackStates.paused:
      return 'paused';
    case PlaybackStates.loading:
    case PlaybackStates.waiting:
    case PlaybackStates.seeking:
    case PlaybackStates.stalled:
      return 'loading';
    case PlaybackStates.stopped:
    case PlaybackStates.ended:
    case PlaybackStates.completed:
      return 'stopped';
    default:
      return 'none';
  }
}

export function useMusicKit(): UseMusicKitReturn {
  const [state, setState] = useState<MusicKitState>({
    isConfigured: false,
    isAuthorized: false,
    isLoading: false,
    error: null,
    storefrontId: null,
    // Playback state
    playbackState: 'none',
    isPlaying: false,
    isPreviewMode: false,
    currentTrackIndex: 0,
    currentArtworkUrl: null,
  });

  const musicKitRef = useRef<typeof window.MusicKit | null>(null);
  const instanceRef = useRef<ReturnType<typeof window.MusicKit.getInstance> | null>(null);

  // Load MusicKit JS script (skip in dev mode)
  useEffect(() => {
    if (DEV_MODE) {
      return;
    }

    const existingScript = document.querySelector(`script[src="${MUSICKIT_SCRIPT_URL}"]`);
    if (existingScript) {
      return;
    }

    const script = document.createElement('script');
    script.src = MUSICKIT_SCRIPT_URL;
    script.async = true;
    script.onload = () => {
      musicKitRef.current = window.MusicKit;
    };
    script.onerror = () => {
      setState(prev => ({ ...prev, error: 'Failed to load MusicKit JS' }));
    };
    document.head.appendChild(script);

    return () => {
      // Don't remove script on cleanup
    };
  }, []);

  // Fetch developer token from API
  const fetchDeveloperToken = useCallback(async (): Promise<string> => {
    const response = await fetch('/api/token');
    if (!response.ok) {
      throw new Error('Failed to fetch developer token');
    }
    const data = await response.json();
    return data.token;
  }, []);

  // Configure MusicKit
  const configure = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    // In dev mode, skip real MusicKit configuration
    if (DEV_MODE) {
      initDevMode();
      setState(prev => ({
        ...prev,
        isConfigured: true,
        isLoading: false,
        isAuthorized: mockMusicKitInstance.isAuthorized,
        storefrontId: mockMusicKitInstance.storefrontId,
      }));
      return;
    }

    try {
      if (!window.MusicKit) {
        throw new Error('MusicKit not loaded');
      }

      const developerToken = await fetchDeveloperToken();

      const instance = await window.MusicKit.configure({
        developerToken,
        app: {
          name: 'heal.fm',
          build: '1.0.0',
        },
      });

      instanceRef.current = instance;
      musicKitRef.current = window.MusicKit;

      setState(prev => ({
        ...prev,
        isConfigured: true,
        isLoading: false,
        isAuthorized: instance.isAuthorized,
        storefrontId: instance.storefrontId || null,
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Configuration failed',
      }));
    }
  }, [fetchDeveloperToken]);

  // Authorize user with Apple Music
  const authorize = useCallback(async (): Promise<boolean> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    // In dev mode, mock authorization
    if (DEV_MODE) {
      await mockMusicKitInstance.authorize();
      setState(prev => ({
        ...prev,
        isLoading: false,
        isAuthorized: true,
        storefrontId: mockMusicKitInstance.storefrontId,
      }));
      return true;
    }

    try {
      const instance = instanceRef.current ?? window.MusicKit?.getInstance();
      if (!instance) {
        throw new Error('MusicKit not configured');
      }

      await instance.authorize();
      const isAuthorized = instance.isAuthorized;

      setState(prev => ({
        ...prev,
        isLoading: false,
        isAuthorized,
        storefrontId: instance.storefrontId || null,
      }));
      return isAuthorized;
    } catch (error) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Authorization failed',
      }));
      return false;
    }
  }, []);

  // Unauthorize user from Apple Music
  const unauthorize = useCallback(async () => {
    // In dev mode, mock unauthorization
    if (DEV_MODE) {
      mockMusicKitInstance.isAuthorized = false;
      setState(prev => ({
        ...prev,
        isAuthorized: false,
        storefrontId: null,
        playbackState: 'none',
        isPlaying: false,
        currentTrackIndex: 0,
        currentArtworkUrl: null,
      }));
      return;
    }

    try {
      const instance = instanceRef.current ?? window.MusicKit?.getInstance();
      if (instance) {
        await instance.unauthorize();
      }

      setState(prev => ({
        ...prev,
        isAuthorized: false,
        storefrontId: null,
        playbackState: 'none',
        isPlaying: false,
        currentTrackIndex: 0,
        currentArtworkUrl: null,
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to disconnect',
      }));
    }
  }, []);

  // Resolve ISRCs to Apple Music catalog IDs
  const resolveIsrcs = useCallback(async (isrcs: string[]): Promise<TrackResolutionResult> => {
    // In dev mode, return mock resolution
    if (DEV_MODE) {
      return mockResolveIsrcs(isrcs);
    }

    const instance = instanceRef.current ?? window.MusicKit?.getInstance();
    if (!instance || !instance.storefrontId) {
      throw new Error('MusicKit not authorized');
    }

    const response = await fetch('/api/resolve-tracks', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        isrcs,
        storefront: instance.storefrontId,
        musicUserToken: await getMusicUserToken(),
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to resolve tracks');
    }

    return response.json();
  }, []);

  // Create playlist in user's Apple Music library
  const createPlaylist = useCallback(async (playlist: GeneratedPlaylist): Promise<string | null> => {
    // In dev mode, return mock playlist
    if (DEV_MODE) {
      setState(prev => ({ ...prev, isLoading: true, error: null }));
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 500));
      const result = mockCreatePlaylist(playlist);
      setState(prev => ({ ...prev, isLoading: false }));
      return result.id;
    }

    const instance = instanceRef.current ?? window.MusicKit?.getInstance();
    if (!instance || !instance.isAuthorized) {
      throw new Error('MusicKit not authorized');
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      // First, resolve ISRCs to catalog IDs
      const isrcs = playlist.tracks.map(t => t.isrc);
      const resolution = await resolveIsrcs(isrcs);

      // Filter to only resolved tracks
      const trackData = playlist.tracks
        .filter(t => resolution.resolved[t.isrc])
        .map(t => ({
          id: resolution.resolved[t.isrc],
          type: 'songs' as const,
        }));

      if (trackData.length === 0) {
        throw new Error('No tracks could be resolved');
      }

      // Create the playlist
      const musicUserToken = await getMusicUserToken();
      const response = await fetch('https://api.music.apple.com/v1/me/library/playlists', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${await getDeveloperToken()}`,
          'Music-User-Token': musicUserToken,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          attributes: {
            name: playlist.name,
            description: playlist.description,
          },
          relationships: {
            tracks: {
              data: trackData,
            },
          },
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.errors?.[0]?.detail || 'Failed to create playlist');
      }

      const data = await response.json();
      setState(prev => ({ ...prev, isLoading: false }));

      return data.data?.[0]?.id || null;
    } catch (error) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to create playlist',
      }));
      return null;
    }
  }, [resolveIsrcs]);

  // Listen for authorization status changes (handles persisted auth restoration)
  useEffect(() => {
    if (DEV_MODE) return;

    const instance = instanceRef.current ?? window.MusicKit?.getInstance();
    if (!instance) return;

    const handleAuthorizationStatusChange = () => {
      const inst = instanceRef.current ?? window.MusicKit?.getInstance();
      if (!inst) return;

      setState(prev => ({
        ...prev,
        isAuthorized: inst.isAuthorized,
        storefrontId: inst.storefrontId || null,
      }));
    };

    instance.addEventListener('authorizationStatusDidChange', handleAuthorizationStatusChange);

    return () => {
      instance.removeEventListener('authorizationStatusDidChange', handleAuthorizationStatusChange);
    };
  }, [state.isConfigured]);

  // Set up playback event listeners
  useEffect(() => {
    const instance = instanceRef.current ?? window.MusicKit?.getInstance();
    if (!instance) return;

    const handlePlaybackStateChange = (event: unknown) => {
      const playbackEvent = event as { state: number };
      const newState = mapPlaybackState(playbackEvent.state);
      setState(prev => ({
        ...prev,
        playbackState: newState,
        isPlaying: newState === 'playing',
      }));
    };

    const handleNowPlayingItemChange = () => {
      const inst = instanceRef.current ?? window.MusicKit?.getInstance();
      if (!inst) return;

      const queue = (inst as unknown as { queue: { position: number } }).queue;
      const nowPlaying = inst.nowPlayingItem;
      // MusicKit provides artworkURL with {w}x{h} template
      const rawArtworkUrl = nowPlaying?.artworkURL ??
        (nowPlaying as unknown as { artwork?: { url?: string } })?.artwork?.url;
      const artworkUrl = rawArtworkUrl
        ?.replace('{w}', '300')
        .replace('{h}', '300') ?? null;

      setState(prev => ({
        ...prev,
        currentTrackIndex: queue?.position ?? 0,
        currentArtworkUrl: artworkUrl,
      }));
    };

    // Add event listeners
    instance.addEventListener('playbackStateDidChange', handlePlaybackStateChange);
    instance.addEventListener('nowPlayingItemDidChange', handleNowPlayingItemChange);
    instance.addEventListener('queuePositionDidChange', handleNowPlayingItemChange);

    return () => {
      instance.removeEventListener('playbackStateDidChange', handlePlaybackStateChange);
      instance.removeEventListener('nowPlayingItemDidChange', handleNowPlayingItemChange);
      instance.removeEventListener('queuePositionDidChange', handleNowPlayingItemChange);
    };
  }, [state.isConfigured]);

  // Detect preview mode (non-subscriber)
  useEffect(() => {
    const instance = instanceRef.current ?? window.MusicKit?.getInstance();
    if (!instance) return;

    // Check if user has an active subscription
    // Non-subscribers can only play 30-second previews
    const checkSubscription = async () => {
      try {
        // If not authorized, definitely preview mode
        if (!instance.isAuthorized) {
          setState(prev => ({ ...prev, isPreviewMode: true }));
          return;
        }

        // Check subscription status via the API
        const musicUserToken = await getMusicUserToken();
        const developerToken = await getDeveloperToken();

        const response = await fetch('https://api.music.apple.com/v1/me/storefront', {
          headers: {
            'Authorization': `Bearer ${developerToken}`,
            'Music-User-Token': musicUserToken,
          },
        });

        if (response.ok) {
          // If we can access user storefront, they have a subscription
          setState(prev => ({ ...prev, isPreviewMode: false }));
        } else {
          setState(prev => ({ ...prev, isPreviewMode: true }));
        }
      } catch {
        // If we can't determine, assume preview mode for safety
        setState(prev => ({ ...prev, isPreviewMode: true }));
      }
    };

    if (state.isAuthorized) {
      checkSubscription();
    }
  }, [state.isAuthorized]);

  // Playback: Set queue with catalog IDs
  const setQueue = useCallback(async (catalogIds: string[]) => {
    // In dev mode, just log and return
    if (DEV_MODE) {
      console.log('[DEV MODE] Setting queue with', catalogIds.length, 'tracks');
      return;
    }

    const instance = instanceRef.current ?? window.MusicKit?.getInstance();
    if (!instance) {
      throw new Error('MusicKit not configured');
    }

    try {
      await instance.setQueue({ songs: catalogIds });
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to set queue',
      }));
      throw error;
    }
  }, []);

  // Playback: Play
  const play = useCallback(async () => {
    // In dev mode, simulate playing state
    if (DEV_MODE) {
      console.log('[DEV MODE] Play requested');
      setState(prev => ({ ...prev, playbackState: 'playing', isPlaying: true }));
      return;
    }

    const instance = instanceRef.current ?? window.MusicKit?.getInstance();
    if (!instance) {
      throw new Error('MusicKit not configured');
    }

    try {
      await instance.play();
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to play',
      }));
      throw error;
    }
  }, []);

  // Playback: Pause
  const pause = useCallback(() => {
    // In dev mode, simulate paused state
    if (DEV_MODE) {
      console.log('[DEV MODE] Pause requested');
      setState(prev => ({ ...prev, playbackState: 'paused', isPlaying: false }));
      return;
    }

    const instance = instanceRef.current ?? window.MusicKit?.getInstance();
    if (!instance) return;

    instance.pause();
  }, []);

  // Playback: Skip to next track
  const skipToNext = useCallback(async () => {
    // In dev mode, simulate track change
    if (DEV_MODE) {
      console.log('[DEV MODE] Skip to next requested');
      setState(prev => ({
        ...prev,
        currentTrackIndex: prev.currentTrackIndex + 1,
      }));
      return;
    }

    const instance = instanceRef.current ?? window.MusicKit?.getInstance();
    if (!instance) return;

    try {
      await instance.skipToNextItem();
    } catch (error) {
      // Ignore errors when at end of queue
      console.warn('Skip to next failed:', error);
    }
  }, []);

  // Playback: Skip to previous track
  const skipToPrevious = useCallback(async () => {
    // In dev mode, simulate track change
    if (DEV_MODE) {
      console.log('[DEV MODE] Skip to previous requested');
      setState(prev => ({
        ...prev,
        currentTrackIndex: Math.max(0, prev.currentTrackIndex - 1),
      }));
      return;
    }

    const instance = instanceRef.current ?? window.MusicKit?.getInstance();
    if (!instance) return;

    try {
      await instance.skipToPreviousItem();
    } catch (error) {
      // Ignore errors when at start of queue
      console.warn('Skip to previous failed:', error);
    }
  }, []);

  // Playback: Skip to specific track by index
  const skipToTrack = useCallback(async (index: number) => {
    // In dev mode, simulate track change
    if (DEV_MODE) {
      console.log('[DEV MODE] Skip to track', index, 'requested');
      setState(prev => ({
        ...prev,
        currentTrackIndex: index,
      }));
      return;
    }

    const instance = instanceRef.current ?? window.MusicKit?.getInstance();
    if (!instance) return;

    try {
      await instance.changeToMediaAtIndex(index);
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to skip to track',
      }));
    }
  }, []);

  return {
    ...state,
    configure,
    authorize,
    unauthorize,
    createPlaylist,
    resolveIsrcs,
    // Playback methods
    setQueue,
    play,
    pause,
    skipToNext,
    skipToPrevious,
    skipToTrack,
  };
}

// Helper to get Music User Token
async function getMusicUserToken(): Promise<string> {
  const instance = window.MusicKit?.getInstance();
  if (!instance) {
    throw new Error('MusicKit not initialized');
  }
  // The musicUserToken is available after authorization
  return (instance as unknown as { musicUserToken: string }).musicUserToken;
}

// Helper to get Developer Token
async function getDeveloperToken(): Promise<string> {
  const response = await fetch('/api/token');
  if (!response.ok) {
    throw new Error('Failed to fetch developer token');
  }
  const data = await response.json();
  return data.token;
}
